/** API client. The browser talks ONLY to our Worker API with a session token
 * (Supabase Auth JWT in production, operator key during the pilot).
 * No Supabase service keys or source API keys ever reach this code. */

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export function getToken(): string {
  return localStorage.getItem('radar_token') ?? '';
}
export function setToken(token: string): void {
  localStorage.setItem('radar_token', token);
}
export function clearToken(): void {
  localStorage.removeItem('radar_token');
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(String(body['error'] ?? `Request failed (${res.status})`), res.status);
  }
  return body as T;
}

export interface Opportunity {
  id: string;
  title: string;
  summary: string | null;
  band: 'HOT' | 'HIGH' | 'MEDIUM' | 'LOW';
  score: number;
  status: string;
  opportunity_type: string;
  customer_type: string;
  location_text: string | null;
  est_value_low: number | null;
  est_value_high: number | null;
  response_deadline: string | null;
  published_at: string | null;
  urgency: string;
  intent: string;
  recurring_potential: string;
  corroboration_count: number;
  signal_count: number;
  primary_source_id: string;
  source_url: string | null;
  explanation: string | null;
  external_ref: string | null;
  is_demo: boolean;
}

export interface Account {
  id: string;
  display_name: string;
  account_score: number;
  tier: 'CHASE_ACCOUNT' | 'HIGH_VALUE' | 'WATCH' | 'JOB_ONLY';
  property_count: number;
  commercial_count: number;
  active_opportunities: number;
  is_verified: boolean;
  phone: string | null;
  website: string | null;
  business_address: string | null;
  rating: number | null;
  rating_count: number | null;
  recommendation: string | null;
  portfolio_value_total: number | null;
  is_demo: boolean;
}

export interface SourceHealthRow {
  id: string;
  name: string;
  status: string;
  state: string;
  refreshFrequency: string | null;
  lastAttemptedRun: string | null;
  lastSuccessfulRun: string | null;
  lastError: string | null;
  recordsReceived: number;
  recordsNew: number;
  recordsPromoted: number;
  notes: string | null;
}

export const api = {
  metrics: () =>
    request<{ activeOpportunities: number; hotOpportunities: number; knownPotentialValue: number }>(
      '/v1/opportunities/metrics',
    ),
  opportunities: (params: Record<string, string>) =>
    request<{ opportunities: Opportunity[] }>(
      `/v1/opportunities?${new URLSearchParams(params)}`,
    ),
  opportunity: (id: string) => request<{
    opportunity: Opportunity;
    propertyIntelligence: unknown[];
    history: Array<{ action: string; note: string | null; created_at: string }>;
    notice: string;
  }>(`/v1/opportunities/${id}`),
  action: (id: string, action: string, note?: string) =>
    request<{ ok: boolean }>(`/v1/opportunities/${id}/actions`, {
      method: 'POST',
      body: JSON.stringify({ action, note }),
    }),
  researchProperty: (id: string) =>
    request<{ found: boolean; propertyIntelligence?: Record<string, unknown>; message?: string; notice?: string }>(
      `/v1/opportunities/${id}/research-property`, { method: 'POST' },
    ),
  researchAccount: (id: string) =>
    request<{ status: string; accountId?: string; message: string }>(
      `/v1/opportunities/${id}/research-account`, { method: 'POST' },
    ),
  crmPush: (id: string) =>
    request<{ ok: boolean; alreadyPushed?: boolean; message?: string }>(
      `/v1/opportunities/${id}/crm-push`, { method: 'POST' },
    ),
  accounts: (params: Record<string, string>) =>
    request<{ accounts: Account[] }>(`/v1/accounts?${new URLSearchParams(params)}`),
  account: (id: string) => request<{
    account: Account & { score_breakdown: Record<string, number> };
    portfolio: Array<Record<string, unknown>>;
    opportunities: Array<Record<string, unknown>>;
    timeline: Array<{ action: string; note: string | null; created_at: string }>;
    notes: Array<{ id: number; note: string; created_at: string }>;
  }>(`/v1/accounts/${id}`),
  addNote: (accountId: string, note: string) =>
    request<{ ok: boolean }>(`/v1/accounts/${accountId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  sourceHealth: () => request<{ sources: SourceHealthRow[] }>('/v1/sources/health'),
};

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

export function fmtValueRange(low: number | null, high: number | null): string {
  if (low == null && high == null) return 'Value unknown';
  if (low != null && high != null && low !== high) return `${fmtMoney(low)}–${fmtMoney(high)}`;
  return fmtMoney(high ?? low);
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
